// ============================================
// Chat Module - Session Modal (ES6)
// 세션 목록 모달 (복제, 삭제, 선택 기능)
// ============================================

import { getCurrentUser } from '../app/state.js';
import { loadSession } from './load.js';
import { authFetch } from '../shared/auth-fetch.js';
import { CHAT_ENDPOINTS } from '../shared/api-endpoints.js';
import { escapeHtml, formatChatDate, showToast, showGlobalLoading, hideGlobalLoading } from '../utils.js';
import { showConfirm } from '../shared/confirm-modal.js';
import { invalidateChatCache, loadChatList, removeRoomFromList } from './list.js';
import { ModalManager } from '../modal-manager.js';
import { useChatStatusStore } from '../../stores/chatStatusStore.js';

// ============================================
// 모달 상태
// ============================================
let currentRoomId = null;
let currentRoomTitle = null;
let isEditMode = false;
let selectedSessions = new Set();

// ============================================
// 세션 목록 모달 표시
// ============================================

export async function showSessionModal(roomId, title = '상담 목록') {
  currentRoomId = roomId;
  currentRoomTitle = title;

  showGlobalLoading();

  // 기존 모달 제거
  const existing = document.getElementById('sessionModal');
  if (existing) existing.remove();

  // 모달 생성 (confirm-modal 패턴 따름)
  const modal = document.createElement('div');
  modal.id = 'sessionModal';
  modal.className = 'session-modal-overlay';
  modal.innerHTML = `
    <div class="session-modal-backdrop" data-action="closeSessionModal"></div>
    <div class="session-modal-content">
      <div class="session-modal-header">
        <h3>${escapeHtml(title)}</h3>
        <div class="session-modal-header-actions">
          <button class="session-edit-btn" data-action="toggleEditMode">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
          <button class="close-btn" data-action="closeSessionModal">&times;</button>
        </div>
      </div>
      <div id="sessionList" class="session-list">
        <div class="session-list-loading">
          <div class="spinner"></div>
        </div>
      </div>
      <div id="sessionEditFooter" class="session-edit-footer" style="display: none;">
        <button class="session-delete-selected-btn" data-action="deleteSelected" disabled>선택 삭제</button>
      </div>
      <button class="new-session-btn" data-action="newSession">+ 새 상담</button>
    </div>
  `;

  document.body.appendChild(modal);

  // 이벤트 바인딩
  setupSessionModalEvents(modal);

  // 세션 목록 로드
  await loadSessionList(roomId);

  hideGlobalLoading();

  // ModalManager 등록 및 표시
  ModalManager.register('sessionModal', closeSessionModal, 'modal');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('open');
      ModalManager.push('sessionModal');
    });
  });
}

// ============================================
// 세션 목록 로드
// ============================================

async function loadSessionList(roomId) {
  const sessionList = document.getElementById('sessionList');
  if (!sessionList) return;

  const user = getCurrentUser();
  if (!user) return;

  try {
    const db = firebase.firestore();
    const sessionsSnap = await db.collection('users').doc(user.uid)
      .collection('chatRooms').doc(roomId)
      .collection('sessions')
      .orderBy('updatedAt', 'desc')
      .get();

    if (sessionsSnap.empty) {
      sessionList.innerHTML = '<div class="session-empty">상담이 없습니다</div>';
      return;
    }

    sessionList.innerHTML = '';

    const chatStatus = useChatStatusStore.getState();

    sessionsSnap.docs.forEach((doc, index) => {
      const data = doc.data();
      const sessionId = doc.id;

      // pending 상태 확인
      const isPending = chatStatus.isPending(sessionId);

      // 마지막 메시지 정리 (응답 메시지만 표시)
      let lastMsgPreview = '';
      const lastMsg = data.lastMessage;
      // role이 'model' 또는 'assistant'인 경우만 표시 (유저 메시지 제외)
      if (lastMsg && (lastMsg.role === 'model' || lastMsg.role === 'assistant')) {
        const msgContent = lastMsg.content || '';
        const charName = currentRoomTitle || '점술가';  // 모달 제목이 점술가 이름
        lastMsgPreview = escapeHtml(msgContent
          .replace(/\{I\}[^\s{}\n]+/g, '')  // 이미지 태그 완전 제거
          .replace(/@silently[\s\S]*?@\/silently/g, '')
          .replace(/\{[A-U]\}/g, '')  // 단일 문자 마커 제거 ({N}, {C}, {S}, {T} 등)
          .replace(/\{\{user\}\}/gi, data.userProfile?.name || '유저')
          .replace(/\{\{char\}\}/gi, charName)  // {{char}} 마커를 캐릭터 이름으로
          .replace(/\{\{[^}]+\}\}/g, '')  // 기타 {{...}} 마커 제거
          .replace(/\*\*([^*]+)\*\*/g, '$1')
          .replace(/\*([^*]+)\*/g, '$1')
          .replace(/\n+/g, ' ')
          .replace(/\s+/g, ' ')  // 다중 공백 제거
          .trim()
          .substring(0, 120));
        if (msgContent.length > 120) lastMsgPreview += '...';
      }

      // pending 중이면 preview에 로딩 표시
      let previewHtml = '';
      if (isPending) {
        previewHtml = `<div class="session-preview pending-preview"><span class="session-pending-dots"><span></span><span></span><span></span></span></div>`;
      } else if (lastMsgPreview) {
        previewHtml = `<div class="session-preview">${lastMsgPreview}</div>`;
      }

      const item = document.createElement('div');
      item.className = `session-item${isPending ? ' pending' : ''}${isEditMode ? ' edit-mode' : ''}`;
      item.dataset.sessionId = sessionId;
      item.dataset.sessionName = data.name || `상담 ${index + 1}`;
      item.innerHTML = `
        <label class="session-checkbox-wrapper">
          <input type="checkbox" class="session-checkbox" data-session-id="${sessionId}" ${selectedSessions.has(sessionId) ? 'checked' : ''}>
          <span class="session-checkbox-custom"></span>
        </label>
        <div class="session-item-main" data-action="selectSession" data-session-id="${sessionId}">
          <div class="session-item-header">
            <div class="session-name-group">
              <span class="session-name">${escapeHtml(data.name || `상담 ${index + 1}`)}</span>
              <span class="session-turn">턴 ${data.turnCount || 0}</span>
            </div>
            <span class="session-date">${formatChatDate(data.updatedAt?.toDate?.() || null)}</span>
          </div>
          ${previewHtml}
        </div>
      `;

      sessionList.appendChild(item);
    });
  } catch (error) {
    console.error('[SessionModal] 세션 목록 로드 실패:', error);
    sessionList.innerHTML = '<div class="session-error">상담을 불러올 수 없습니다</div>';
  }
}

// ============================================
// 이벤트 핸들러
// ============================================

function setupSessionModalEvents(modal) {
  // 길게 누르기 이벤트 (메뉴 열기)
  let pressTimer = null;
  let isPressing = false;
  let pressTarget = null;

  const sessionList = modal.querySelector('#sessionList');

  const clearPressTimer = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      pressTimer = null;
    }
    isPressing = false;
    pressTarget = null;
  };

  const startPress = (item) => {
    if (!item || isEditMode) return;
    isPressing = true;
    pressTarget = item;
    pressTimer = setTimeout(() => {
      if (isPressing && pressTarget) {
        const sessionId = pressTarget.dataset.sessionId;
        const sessionName = pressTarget.dataset.sessionName || '상담';
        showSessionMenuSheet(sessionId, sessionName);
        isPressing = false;
        pressTarget = null;
      }
    }, 500);
  };

  // 터치 이벤트 (모바일)
  sessionList?.addEventListener('touchstart', (e) => {
    const item = e.target.closest('.session-item');
    startPress(item);
  }, { passive: true });

  sessionList?.addEventListener('touchend', clearPressTimer, { passive: true });
  sessionList?.addEventListener('touchmove', clearPressTimer, { passive: true });
  sessionList?.addEventListener('touchcancel', clearPressTimer, { passive: true });

  // 마우스 이벤트 (데스크톱)
  sessionList?.addEventListener('mousedown', (e) => {
    // 왼쪽 클릭만
    if (e.button !== 0) return;
    const item = e.target.closest('.session-item');
    startPress(item);
  });

  sessionList?.addEventListener('mouseup', clearPressTimer);
  sessionList?.addEventListener('mouseleave', clearPressTimer);

  modal.addEventListener('click', async (e) => {
    const action = e.target.closest('[data-action]')?.dataset.action;
    if (!action) return;

    const sessionId = e.target.closest('[data-session-id]')?.dataset.sessionId;

    switch (action) {
      case 'closeSessionModal':
        closeSessionModal();
        break;

      case 'selectSession':
        // 편집 모드에서는 선택 무시
        if (isEditMode) return;
        if (sessionId) {
          closeSessionModal();
          await loadSession(currentRoomId, sessionId);
        }
        break;

      case 'deleteSession':
        if (sessionId) {
          const sessionItem = e.target.closest('.session-item');
          const sessionName = sessionItem?.dataset.sessionName || '상담';
          await handleDeleteSession(sessionId, sessionName);
        }
        break;

      case 'newSession':
        await handleNewSession();
        break;

      case 'toggleEditMode':
        toggleEditMode();
        break;

      case 'deleteSelected':
        await handleDeleteSelectedSessions();
        break;
    }
  });

  // 체크박스 이벤트 (이벤트 위임)
  modal.addEventListener('change', (e) => {
    if (e.target.classList.contains('session-checkbox')) {
      const sessionId = e.target.dataset.sessionId;
      if (e.target.checked) {
        selectedSessions.add(sessionId);
      } else {
        selectedSessions.delete(sessionId);
      }
      updateDeleteSelectedButton();
    }
  });
}

// ============================================
// 세션 선택
// ============================================

export async function selectSession(roomId, sessionId) {
  closeSessionModal();
  await loadSession(roomId, sessionId);
}

// ============================================
// 세션 복제
// ============================================

async function handleDuplicateSession(sessionId, sessionName) {
  const confirmed = await showConfirm({
    message: `"${sessionName}" 상담을 복제하시겠습니까?\n현재 상태 그대로 새 상담이 생성됩니다.`,
    confirmText: '복제'
  });
  if (!confirmed) return;

  showGlobalLoading();
  try {
    const response = await authFetch(CHAT_ENDPOINTS.duplicateSession, {
      body: { roomId: currentRoomId, sessionId }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '복제 실패');
    }

    const result = await response.json();
    showToast(`"${result.sessionName}" 상담이 생성되었습니다`, 'success');

    // 세션 목록 새로고침
    await loadSessionList(currentRoomId);
    invalidateChatCache();
    loadChatList(0, true);
  } catch (error) {
    console.error('[SessionModal] 복제 실패:', error);
    showToast(error.message || '상담 복제에 실패했습니다', 'error');
  } finally {
    hideGlobalLoading();
  }
}

// ============================================
// 세션 삭제
// ============================================

async function handleDeleteSession(sessionId, sessionName) {
  const confirmed = await showConfirm(`"${sessionName}" 상담을 삭제하시겠습니까?`, '상담 삭제');
  if (!confirmed) return;

  showGlobalLoading();
  try {
    const response = await authFetch(CHAT_ENDPOINTS.deleteSession, {
      body: { roomId: currentRoomId, sessionId }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '삭제 실패');
    }

    const result = await response.json();
    showToast('상담이 삭제되었습니다', 'success');

    // 모든 세션이 삭제되면 모달 닫고 리스트에서 room 제거
    if (result.roomDeleted) {
      closeSessionModal();
      removeRoomFromList(currentRoomId);
    } else {
      // 세션 목록 새로고침
      await loadSessionList(currentRoomId);
    }

    invalidateChatCache();
    loadChatList(0, true);
  } catch (error) {
    console.error('[SessionModal] 삭제 실패:', error);
    showToast(error.message || '상담 삭제에 실패했습니다', 'error');
  } finally {
    hideGlobalLoading();
  }
}

// ============================================
// 새 세션 시작
// ============================================

async function handleNewSession() {
  // chatRoom에서 publishedId 가져오기
  const user = getCurrentUser();
  if (!user) return;

  try {
    const db = firebase.firestore();
    const roomDoc = await db.collection('users').doc(user.uid)
      .collection('chatRooms').doc(currentRoomId).get();

    if (!roomDoc.exists) {
      showToast('채팅방을 찾을 수 없습니다', 'error');
      return;
    }

    const roomData = roomDoc.data();
    const publishedId = roomData.publishedId;

    if (!publishedId) {
      showToast('콘텐츠 정보가 없습니다', 'error');
      return;
    }

    closeSessionModal();
    showGlobalLoading();

    const response = await authFetch(CHAT_ENDPOINTS.startPublishedChat, {
      body: { contentId: publishedId }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '상담 생성 실패');
    }

    const result = await response.json();
    hideGlobalLoading();
    showToast(`"${result.sessionName}" 상담이 생성되었습니다`, 'success');

    // 새 세션으로 이동
    await loadSession(result.roomId, result.sessionId);
    invalidateChatCache();
    loadChatList(0, true);
  } catch (error) {
    hideGlobalLoading();
    console.error('[SessionModal] 새 세션 생성 실패:', error);
    showToast(error.message || '새 상담 생성에 실패했습니다', 'error');
  }
}

// ============================================
// 모달 닫기
// ============================================

export function closeSessionModal() {
  const modal = document.getElementById('sessionModal');
  if (!modal) return;

  modal.classList.remove('open');
  ModalManager.remove('sessionModal', true);
  ModalManager.unregister('sessionModal');

  setTimeout(() => {
    modal.remove();
    currentRoomId = null;
    currentRoomTitle = null;
    // 편집 모드 상태 초기화
    isEditMode = false;
    selectedSessions.clear();
  }, 200);
}

// ============================================
// 편집 모드
// ============================================

function toggleEditMode() {
  isEditMode = !isEditMode;
  selectedSessions.clear();

  const modal = document.getElementById('sessionModal');
  const content = modal?.querySelector('.session-modal-content');
  const sessionList = document.getElementById('sessionList');
  const editFooter = document.getElementById('sessionEditFooter');
  const newSessionBtn = modal?.querySelector('.new-session-btn');
  const editBtn = modal?.querySelector('.session-edit-btn');

  if (isEditMode) {
    content?.classList.add('edit-mode');
    sessionList?.querySelectorAll('.session-item').forEach(item => {
      item.classList.add('edit-mode');
    });
    if (editFooter) editFooter.style.display = 'flex';
    if (newSessionBtn) newSessionBtn.style.display = 'none';
    if (editBtn) editBtn.innerHTML = `<span style="font-size: 14px; font-weight: 500;">완료</span>`;
  } else {
    content?.classList.remove('edit-mode');
    sessionList?.querySelectorAll('.session-item').forEach(item => {
      item.classList.remove('edit-mode');
      const checkbox = item.querySelector('.session-checkbox');
      if (checkbox) checkbox.checked = false;
    });
    if (editFooter) editFooter.style.display = 'none';
    if (newSessionBtn) newSessionBtn.style.display = 'block';
    if (editBtn) editBtn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
      </svg>
    `;
  }

  updateDeleteSelectedButton();
}

function updateDeleteSelectedButton() {
  const deleteBtn = document.querySelector('.session-delete-selected-btn');
  if (!deleteBtn) return;

  const count = selectedSessions.size;
  deleteBtn.disabled = count === 0;
  deleteBtn.textContent = count > 0 ? `선택 삭제 (${count})` : '선택 삭제';
}

async function handleDeleteSelectedSessions() {
  if (selectedSessions.size === 0) return;

  const count = selectedSessions.size;
  const confirmed = await showConfirm({
    message: `선택한 ${count}개 세션을 삭제하시겠습니까?\n삭제된 세션은 복구할 수 없습니다.`,
    confirmText: '삭제',
    isDanger: true
  });
  if (!confirmed) return;

  showGlobalLoading();
  let successCount = 0;
  let roomDeleted = false;

  try {
    for (const sessionId of selectedSessions) {
      const response = await authFetch(CHAT_ENDPOINTS.deleteSession, {
        body: { roomId: currentRoomId, sessionId }
      });

      if (response.ok) {
        successCount++;
        const result = await response.json();
        if (result.roomDeleted) {
          roomDeleted = true;
          break;
        }
      }
    }

    if (roomDeleted) {
      closeSessionModal();
      removeRoomFromList(currentRoomId);
      showToast('모든 세션이 삭제되었습니다', 'success');
    } else {
      showToast(`${successCount}개 세션이 삭제되었습니다`, 'success');
      selectedSessions.clear();
      await loadSessionList(currentRoomId);
      toggleEditMode();
    }

    invalidateChatCache();
    loadChatList(0, true);
  } catch (error) {
    console.error('[SessionModal] 선택 삭제 실패:', error);
    showToast('일부 세션 삭제에 실패했습니다', 'error');
  } finally {
    hideGlobalLoading();
  }
}

// ============================================
// 세션 메뉴 바텀시트
// ============================================

let menuTargetSessionId = null;
let menuTargetSessionName = null;

function showSessionMenuSheet(sessionId, sessionName) {
  menuTargetSessionId = sessionId;
  menuTargetSessionName = sessionName;

  const existing = document.getElementById('sessionMenuSheet');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'sessionMenuSheet';
  modal.className = 'bottom-sheet-overlay';

  const backdrop = document.createElement('div');
  backdrop.className = 'bottom-sheet-backdrop';
  backdrop.dataset.action = 'closeMenu';

  const sheet = document.createElement('div');
  sheet.className = 'bottom-sheet session-menu-sheet';

  const handle = document.createElement('div');
  handle.className = 'bottom-sheet-handle';

  const body = document.createElement('div');
  body.className = 'bottom-sheet-body';
  body.style.padding = '8px 16px 24px 16px';

  const buttonsWrapper = document.createElement('div');
  buttonsWrapper.className = 'session-menu-buttons';

  // 복제 버튼
  const duplicateBtn = document.createElement('button');
  duplicateBtn.className = 'session-menu-btn';
  duplicateBtn.dataset.action = 'duplicateSession';
  duplicateBtn.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
    <span>복제</span>
  `;

  // 이름 변경 버튼
  const renameBtn = document.createElement('button');
  renameBtn.className = 'session-menu-btn';
  renameBtn.dataset.action = 'renameSession';
  renameBtn.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
    <span>이름 변경</span>
  `;

  // 삭제 버튼
  const deleteBtn = document.createElement('button');
  deleteBtn.className = 'session-menu-btn danger';
  deleteBtn.dataset.action = 'deleteSession';
  deleteBtn.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
    <span>삭제</span>
  `;

  buttonsWrapper.appendChild(renameBtn);
  buttonsWrapper.appendChild(duplicateBtn);
  buttonsWrapper.appendChild(deleteBtn);
  body.appendChild(buttonsWrapper);
  sheet.appendChild(handle);
  sheet.appendChild(body);
  modal.appendChild(backdrop);
  modal.appendChild(sheet);

  document.body.appendChild(modal);

  modal.addEventListener('click', async (e) => {
    const action = e.target.closest('[data-action]')?.dataset.action;
    if (!action) return;

    switch (action) {
      case 'closeMenu':
        closeSessionMenuSheet();
        break;
      case 'duplicateSession':
        closeSessionMenuSheet();
        await handleDuplicateSession(menuTargetSessionId, menuTargetSessionName);
        break;
      case 'renameSession':
        closeSessionMenuSheet();
        showRenameModal(menuTargetSessionId, menuTargetSessionName);
        break;
      case 'deleteSession':
        closeSessionMenuSheet();
        await handleDeleteSession(menuTargetSessionId, menuTargetSessionName);
        break;
    }
  });

  ModalManager.register('sessionMenuSheet', closeSessionMenuSheet, 'bottomSheet');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('open');
      ModalManager.push('sessionMenuSheet');
    });
  });
}

function closeSessionMenuSheet() {
  const modal = document.getElementById('sessionMenuSheet');
  if (!modal) return;

  modal.classList.remove('open');
  ModalManager.remove('sessionMenuSheet', true);
  ModalManager.unregister('sessionMenuSheet');

  setTimeout(() => {
    modal.remove();
  }, 300);
}

// ============================================
// 세션 이름 변경 바텀시트
// ============================================

// 외부에서 호출 시 roomId 전달 가능
let renameTargetRoomId = null;

export function showRenameModal(sessionId, currentName, roomId = null) {
  renameTargetRoomId = roomId;
  const existing = document.getElementById('sessionRenameSheet');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'sessionRenameSheet';
  modal.className = 'bottom-sheet-overlay';

  const backdrop = document.createElement('div');
  backdrop.className = 'bottom-sheet-backdrop';
  backdrop.dataset.action = 'closeRename';

  const sheet = document.createElement('div');
  sheet.className = 'bottom-sheet session-rename-sheet';

  const handle = document.createElement('div');
  handle.className = 'bottom-sheet-handle';

  const body = document.createElement('div');
  body.className = 'bottom-sheet-body';
  body.style.padding = '16px 20px 24px 20px';

  const title = document.createElement('div');
  title.className = 'rename-sheet-title';
  title.textContent = '세션 이름 변경';

  const input = document.createElement('input');
  input.type = 'text';
  input.id = 'sessionNameInput';
  input.className = 'rename-input';
  input.value = currentName;
  input.maxLength = 30;
  input.placeholder = '세션 이름';

  const confirmBtn = document.createElement('button');
  confirmBtn.className = 'rename-confirm-btn';
  confirmBtn.dataset.action = 'confirmRename';
  confirmBtn.textContent = '변경';

  body.appendChild(title);
  body.appendChild(input);
  body.appendChild(confirmBtn);
  sheet.appendChild(handle);
  sheet.appendChild(body);
  modal.appendChild(backdrop);
  modal.appendChild(sheet);

  document.body.appendChild(modal);

  const handleConfirm = async () => {
    const newName = input.value.trim();
    if (newName && newName !== currentName) {
      closeRenameModal();
      await handleRenameSession(sessionId, newName);
    } else if (!newName) {
      showToast('세션 이름을 입력해주세요', 'error');
    } else {
      closeRenameModal();
    }
  };

  modal.addEventListener('click', async (e) => {
    const action = e.target.closest('[data-action]')?.dataset.action;
    if (!action) return;

    if (action === 'closeRename') {
      closeRenameModal();
    } else if (action === 'confirmRename') {
      await handleConfirm();
    }
  });

  input.addEventListener('keydown', async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleConfirm();
    }
  });

  ModalManager.register('sessionRenameSheet', closeRenameModal, 'bottomSheet');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('open');
      ModalManager.push('sessionRenameSheet');
      input.focus();
      input.select();
    });
  });
}

function closeRenameModal() {
  const modal = document.getElementById('sessionRenameSheet');
  if (!modal) return;

  modal.classList.remove('open');
  ModalManager.remove('sessionRenameSheet', true);
  ModalManager.unregister('sessionRenameSheet');

  setTimeout(() => {
    modal.remove();
  }, 300);
}

// ============================================
// 세션 이름 변경 처리
// ============================================

async function handleRenameSession(sessionId, newName) {
  const targetRoomId = renameTargetRoomId || currentRoomId;
  if (!targetRoomId) {
    showToast('채팅방 정보를 찾을 수 없습니다', 'error');
    return;
  }

  showGlobalLoading();
  try {
    const response = await authFetch(CHAT_ENDPOINTS.renameSession, {
      body: { roomId: targetRoomId, sessionId, newName }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '이름 변경 실패');
    }

    const result = await response.json();
    showToast(`"${result.sessionName}"으로 변경되었습니다`, 'success');

    // 세션 목록 새로고침 (모달이 열려있는 경우)
    if (currentRoomId) {
      await loadSessionList(currentRoomId);
    }

    // 메모리 패널의 세션 이름 업데이트
    const sessionNameEl = document.querySelector('.memory-session-name');
    if (sessionNameEl) {
      sessionNameEl.textContent = result.sessionName;
    }

    // currentChatData 업데이트
    if (window.currentChatData) {
      window.currentChatData.sessionName = result.sessionName;
    }
  } catch (error) {
    console.error('[SessionModal] 이름 변경 실패:', error);
    showToast(error.message || '세션 이름 변경에 실패했습니다', 'error');
  } finally {
    hideGlobalLoading();
    renameTargetRoomId = null;
  }
}

// ============================================
// 세션 모달 배지 실시간 업데이트
// ============================================

export function refreshSessionModalBadges() {
  const sessionList = document.getElementById('sessionList');
  if (!sessionList) return;

  const chatStatus = useChatStatusStore.getState();
  const sessionItems = sessionList.querySelectorAll('.session-item[data-session-id]');

  sessionItems.forEach(item => {
    const sessionId = item.dataset.sessionId;
    const isPending = chatStatus.isPending(sessionId);
    const lastMessage = chatStatus.getLastMessage(sessionId);

    // pending 클래스 토글
    item.classList.toggle('pending', isPending);

    // preview 영역 업데이트
    const itemMain = item.querySelector('.session-item-main');
    if (!itemMain) return;

    let preview = itemMain.querySelector('.session-preview');

    if (isPending) {
      // pending 상태: 기존 미리보기 숨기고 로딩 표시
      if (!preview) {
        preview = document.createElement('div');
        preview.className = 'session-preview pending-preview';
        itemMain.appendChild(preview);
      } else {
        // 기존 preview가 있으면 내용 교체 (기존 메시지 숨김)
        preview.classList.add('pending-preview');
      }
      preview.innerHTML = '<span class="session-pending-dots"><span></span><span></span><span></span></span>';
    } else if (lastMessage) {
      // 완료 상태: 새 메시지 표시
      const userName = item.dataset.userName || '유저';
      const cleanMsg = lastMessage
        .replace(/\{I\}[^\s{}\n]+/g, '')
        .replace(/@silently[\s\S]*?@\/silently/g, '')
        .replace(/\{N\}/g, '').replace(/\{C\}/g, '')
        .replace(/\{\{user\}\}/gi, userName)
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/\n+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 120);

      if (!preview) {
        preview = document.createElement('div');
        preview.className = 'session-preview';
        itemMain.appendChild(preview);
      }
      preview.classList.remove('pending-preview');
      preview.textContent = cleanMsg.length >= 120 ? cleanMsg + '...' : cleanMsg;
    } else if (preview && preview.classList.contains('pending-preview')) {
      // pending 해제됐지만 lastMessage가 없으면 preview 제거 (빈 공간 방지)
      preview.remove();
    }
  });
}
